import {
  actionColumn,
  Button,
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  multiFilter,
  Pagination,
  ScrollableContent,
  simpleHeader,
  Tag,
  toggleFilter,
  Tooltip,
  treeFilter,
  useModal,
  usePersistedFilter,
  useTestIds,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { chipCell, dateCell, SearchBox } from "src/components";
import { LoadingTable } from "src/components/LoadingTable";
import { StatusIndicator } from "src/components/StatusIndicator";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { showPBBv2AcceptUpdates } from "src/features";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  BidPackageBidderKind,
  BidPackageGroupOrderBy,
  BidPackageGroupsFilter,
  BidPackageStatus,
  BidScopeSource,
  FeatureFlagType,
  InternalEstimateType,
  Maybe,
  Order,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import useZodQueryString from "src/hooks/useZodQueryString";
import {
  BidPackageDetailFragment,
  BidPackageGroupDetailFragment,
  BidPackagesPage_BidPackageBidderFragment,
  ProjectBidPackagesPageMetadataFragment,
  useBidPackageGroupsQuery,
  useBidPackagesPageMetadataQuery,
  useDeleteBidPackageMutation,
} from "src/routes/bids/BidPackagesPage.generated";
import {
  createBidPackagesDetailUrl,
  createBidPackagesEditUrl,
  createBidPackageUrl,
  createPlanBasedBidPackageEditUrl,
  createPlanBasedBidPackageUrl,
} from "src/RouteUrls";
import { groupBy, pageSchema, queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { PageHeader } from "../layout/PageHeader";
import { TableActions } from "../layout/TableActions";
import { useProjectContext } from "../projects/context/ProjectContext";
import { UpdatesAvailableButton } from "./components/BidUpdatesAvailable";

export function BidPackagesPage() {
  // Loading markets, projects, and bid categories before rendering the page
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, {
    data: ({ costCodes, projects }) => <BidPackagesPageView projects={projects} costCodes={costCodes} />,
  });
}

export function ProjectBidPackagesPage() {
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, { data: ({ costCodes }) => <BidPackagesPageView costCodes={costCodes} /> });
}

export type BidPackagesPageViewProps = {
  costCodes: NamedFragment[];
  projects?: ProjectBidPackagesPageMetadataFragment[];
};

export function BidPackagesPageView({ costCodes, projects }: BidPackagesPageViewProps) {
  const project = useProjectContext();
  const { projectId } = useRouteParams<{ projectId?: string }>();
  const isPBBv2Enabled = useFeatureFlag(FeatureFlagType.BidPackagesV2);
  const showPlanBasedBidOption = !project.id || isPBBv2Enabled;
  const { openModal } = useModal();
  const [deleteBidPackage] = useDeleteBidPackageMutation({ refetchQueries: ["BidPackageGroups"] });
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const [order, setOrder] = useState<BidPackageGroupOrderBy>({ updatedAt: Order.Desc });
  const filterDefs: FilterDefs<BidPackageGroupsFilter> = useMemo(
    () => ({
      ...(Array.isArray(projects)
        ? {
            project: treeFilter({
              defaultCollapsed: true,
              label: "Markets and Communities",
              filterBy: "leaf",
              getOptionLabel: (o) => o.name,
              getOptionValue: (o) => o.id,
              options: Object.values(
                groupBy(
                  projects.filter((p) => p.market),
                  (p) => p.market!.id,
                ),
              ).map((p) => ({
                id: p[0].market!.id,
                name: p[0].market!.name,
                children: p.map((d) => ({ id: d.id, name: d.name })),
              })),
            }),
          }
        : {}),
      costCode: multiFilter({
        label: "Cost Code",
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
        options: costCodes,
      }),
      // If we are viewing project bid packages, include the toggle to show bids from all levels
      ...(project.id
        ? { includeRelatedProjectBids: toggleFilter({ label: "Show Bids from All Levels", defaultValue: true }) }
        : {}),
    }),
    [projects, costCodes],
  );

  const { filter, setFilter } = usePersistedFilter<BidPackageGroupsFilter>({
    storageKey: "bidPackageGroupsFilter",
    filterDefs,
  });
  const [searchFilter, setSearchFilter] = useState<string>("");

  const { includeRelatedProjectBids, ...additionalFilters } = filter;

  const { loading, data } = useBidPackageGroupsQuery({
    variables: {
      filter: {
        search: searchFilter || undefined,
        project: projectId ? [projectId] : undefined,
        // If we are viewing subdivision bid packages, include child project bids toggle determines if Lot bids are included
        ...(project.__typename === "Subdivision"
          ? {
              scopeSource: includeRelatedProjectBids
                ? [BidScopeSource.Subdivision, BidScopeSource.Lot]
                : [BidScopeSource.Subdivision],
            }
          : { includeRelatedProjectBids }),
        ...additionalFilters,
      },
      orderBy: order,
      packageOrderBy: order,
      page: pageSettings,
      costCode: filter.costCode,
    },
  });

  const onDelete = useCallback(
    (bidPackage: BidPackageDetailFragment) => {
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage={
              <>
                Are you sure you want to delete this bid package <b>{bidPackage.name}</b>? It will not be archived and
                cannot be undone.
              </>
            }
            onConfirmAction={async () => {
              await deleteBidPackage({ variables: { id: bidPackage.id } });
            }}
            title="Delete Bid Package"
            label="Yes, Delete"
          />
        ),
      });
    },
    [deleteBidPackage],
  );

  const columns = useMemo(() => createColumns(onDelete, projectId), [projectId, onDelete]);
  const rows = useMemo(() => createRows(data?.bidPackageGroups.entities || []), [data?.bidPackageGroups]);

  return (
    <div>
      <PageHeader
        title="Bid Packages"
        right={
          <>
            <ButtonMenu
              trigger={{ label: "Create Bid Package", variant: "primary" }}
              items={[
                ...(showPlanBasedBidOption
                  ? [{ label: "Create Plan Based Bid", onClick: createPlanBasedBidPackageUrl({ projectId }) }]
                  : []),
                { label: "Create Historical", onClick: createBidPackageUrl("add", { projectId }) },
                {
                  label: "Create Internal Estimate",
                  onClick: createBidPackageUrl("add", { projectId, isInternalEstimate: true }),
                },
              ]}
            />
          </>
        }
      />
      <TableActions>
        <Filters filter={filter} onChange={setFilter} filterDefs={filterDefs} />
        <SearchBox onSearch={setSearchFilter} />
      </TableActions>
      <ScrollableContent>
        {!loading ? (
          <>
            <GridTable
              columns={columns}
              rows={rows}
              sorting={{
                on: "server",
                onSort: (key, direction) => setOrder(toOrder(key, direction)),
                value: parseOrder(order),
              }}
              stickyHeader
              fallbackMessage="There aren't any bid packages to display."
            />
            <Pagination
              page={[pageSettings, setPageSettings]}
              totalCount={data?.bidPackageGroups.pageInfo.totalCount || 0}
            />
          </>
        ) : (
          <LoadingTable />
        )}
      </ScrollableContent>
    </div>
  );
}

type HeaderRow = { kind: "header" };
type BidPackageGroupedRow = { kind: "group"; data: BidPackageGroupDetailFragment; children: BidPackageRow[] };
type BidPackageRow = { kind: "package"; data: BidPackageDetailFragment };
type Row = HeaderRow | BidPackageGroupedRow | BidPackageRow;

const createColumns = (
  onDelete: (bidPackage: BidPackageDetailFragment) => void,
  projectId?: string,
): GridColumn<Row>[] => [
  collapseColumn<Row>(),
  column<Row>({
    header: "Name",
    group: ({ id, status, displayName, isPlanBasedV2 }) =>
      // Allow edit if the status is draft
      status.code === BidPackageStatus.Draft ? (
        <Button
          variant="text"
          onClick={
            isPlanBasedV2 ? createPlanBasedBidPackageEditUrl(id, projectId) : createBidPackagesEditUrl(id, projectId)
          }
          label={<span css={Css.mdSb.$}>{displayName}</span>}
        />
      ) : (
        { content: displayName, css: Css.mdSb.$ }
      ),
    package: ({ name, bidPackageGroup, id, latestVersion, availableUpdates, costCodesName, isPlanBasedV2 }) => ({
      content: (
        <div css={Css.df.fdr.aic.gap2.$}>
          <Button
            variant="text"
            label={<FormattedBidName costCodesName={costCodesName} latestVersion={latestVersion} />}
            onClick={createBidPackagesDetailUrl(bidPackageGroup!.id, id)}
          />
          {(!isPlanBasedV2 || !showPBBv2AcceptUpdates) && (
            <UpdatesAvailableButton bidPackageId={id} updates={availableUpdates} showText={false} />
          )}
        </div>
      ),
      value: name,
      w: 2,
      css: Css.sm.$,
    }),
  }),
  column<Row>({ header: "Cost Type", group: emptyCell, package: ({ costType }) => costType.name }),
  column<Row>({
    header: "Product Offerings",
    group: emptyCell,
    package: ({ latestVersion, projects }) => ({
      content: <ProductOfferingsBadge latestVersion={latestVersion} projects={projects} />,
    }),
  }),
  column<Row>({
    header: "Hierarchy",
    group: ({ projects, bidPackages }) => bidHierarchyColumn({ projects, scopeSource: bidPackages.first!.scopeSource }),
    package: ({ projects, latestVersion, status, scopeSource }) =>
      latestVersion.version !== 1 || status.code !== BidPackageStatus.Draft
        ? bidHierarchyColumn({ projects, scopeSource })
        : emptyCell,
  }),
  column<Row>({
    header: "Status",
    group: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    package: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    serverSideSortKey: "status",
  }),
  column<Row>({
    header: "Source",
    group: emptyCell,
    package: ({ activeBidders }) => <BidPackageSource bidders={activeBidders} />,
  }),
  dateColumn<Row>({
    header: "Last Edited",
    group: ({ updatedAt }) => dateCell(updatedAt),
    package: ({ updatedAt }) => dateCell(updatedAt),
    serverSideSortKey: "updatedAt",
  }),
  actionColumn<Row>({
    header: emptyCell,
    group: emptyCell,
    package: (data) => ({
      content: data.canDelete.allowed && <IconButton inc={2} icon="trash" onClick={() => onDelete(data)} />,
    }),
    w: "80px",
  }),
];

function createRows(bidPackageGroups: BidPackageGroupDetailFragment[]): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...bidPackageGroups.flatMap((bpg) => {
      const firstBidPackage = bpg.bidPackages.first!;
      // If we have published the first bid package don't show the group, this is because the groups are purely used for bulk bid package creation
      if (
        firstBidPackage.latestVersion.version !== 1 ||
        firstBidPackage.status.code !== BidPackageStatus.Draft ||
        // Ignore PBBv2 bid packages because they are not bulk created.
        bpg.isPlanBasedV2
      ) {
        return bpg.bidPackages.map((bp): GridDataRow<Row> => ({ kind: "package" as const, id: bp.id, data: bp }));
      }
      return [
        {
          kind: "group" as const,
          id: bpg.id,
          data: bpg,
          children: bpg.bidPackages.map((bp) => ({ kind: "package" as const, id: bp.id, data: bp })),
        },
      ];
    }),
  ];
}

// Renders a source column given the bidders for a bid package
// Multiple bidders are stacked vertically.
// Show a tag for bidders kind=InternalEstimate with the estimate type.
function BidPackageSource(props: { bidders: BidPackagesPage_BidPackageBidderFragment[] }) {
  const { bidders } = props;
  if (bidders.isEmpty) {
    return <span css={Css.gray700.$}>No bidders yet</span>;
  }

  const [estimates, trades] = bidders.partition((b) => b.kind === BidPackageBidderKind.InternalEstimate);

  const estimateTags = estimates.map((e) => {
    const isLotDetermined = e.bidContract?.internalEstimateType?.code === InternalEstimateType.LotDetermined;
    return (
      <div key={e.id}>
        <Tag
          key={e.id}
          text={isLotDetermined ? "LOT DETERMINED ESTIMATE" : "INTERNAL ESTIMATE"}
          type={isLotDetermined ? "neutral" : "caution"}
        />
      </div>
    );
  });

  const tradeNames = trades.map((t) => <span key={t.id}>{t.name}</span>);

  return (
    <div css={Css.df.fdc.gap1.$}>
      {estimateTags}
      {tradeNames}
    </div>
  );
}

// Format the long bid name and versions labels
// To fit fixed width columns
export function FormattedBidName({
  costCodesName,
  latestVersion,
}: {
  costCodesName: string;
  latestVersion: { displayVersion: string };
}) {
  return (
    <div css={Css.df.fdc.$}>
      <div css={Css.df.fdc.wsnw.$}>
        {costCodesName.split(",").map((n) => (
          <span key={n}>{n}</span>
        ))}
      </div>
      <span css={Css.xs.$}>{latestVersion.displayVersion}</span>
    </div>
  );
}

// Renders an "X/Y" badge; X: POFs on this bid's latest version, Y: POFs at the community/market scope.
function ProductOfferingsBadge(props: Pick<BidPackageDetailFragment, "latestVersion" | "projects">) {
  const { latestVersion, projects } = props;
  const tid = useTestIds({}, "productOfferingsBadge");
  const onBid = latestVersion.readyPlanAggregateVersions.length;
  const total =
    projects
      .flatMap((p) => {
        if (p.__typename === "Community") return p.productOfferings;
        if (p.__typename === "Market") return p.children.flatMap((c) => c.productOfferings);
        return [];
      })
      .filter((p) => p.status.code === ReadyPlanStatus.Active).length || onBid;
  if (total === 0) return null;
  return (
    <Tooltip
      title={
        <div css={Css.df.fdc.gap1.$}>
          {latestVersion.readyPlanAggregateVersions.map((rpav) => (
            <div key={rpav.id}>
              {rpav.readyPlan.name} {rpav.displayVersion}
            </div>
          ))}
        </div>
      }
      placement="top"
    >
      <Tag {...tid} text={`${onBid === total ? "ALL " : ""}${onBid}/${total}`} type="info" />
    </Tooltip>
  );
}

// For now we don't want to list out all lots or have chips saying "All Lots in subdivision"
export function bidHierarchyColumn({
  projects,
  scopeSource,
}: {
  projects: NamedFragment[];
  scopeSource: Maybe<BidPackageDetailFragment["scopeSource"]>;
}) {
  return scopeSource?.code === BidScopeSource.Lot ? chipCell(["Lot"]) : chipCell(projects.map((p) => p.name));
}
